<template>
  <div class="report-wrapper">
    <div class="header">
      <div class="title-container">
        <span class="title is-5">
          {{ title }}
          <n-tooltip position="right" :text="tooltipText" />
        </span>
      </div>
      <div v-if="loadedAndReturnedData && $slots['header-addons']" class="buttons has-addons">
        <slot name="header-addons"></slot>
      </div>
    </div>

    <div class="chart-inner" v-if="loadedAndReturnedData">
      <slot name="chart" v-bind="{ reportId: computedReportId }"></slot>
    </div>
    <div v-else-if="loadedAndNotReturnedData" class="no-data">
      <ChartNoData />
    </div>
    <div v-else-if="loadingError" class="error-overlay">
      <ChartError />
    </div>
    <div v-else>
      <n-loading-spinner />
    </div>

    <div v-if="loadedAndReturnedData && !noLabels" class="chart-labels">
      <ChartLabels :reportId="computedReportId" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import ChartError from './ChartError.vue';
import ChartNoData from '@/features/reports/components/ChartNoData.vue';
import ChartLabels from '@/features/reports/components/ChartLabels.vue';
import { computed } from 'vue';
import { nanoid } from 'nanoid';

const props = defineProps({
  reportId: {
    type: String,
    required: false,
  },
  dataEmpty: {
    type: Boolean,
    default: false,
  },
  loadedData: {
    type: Boolean,
    default: false,
  },
  loadingError: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    required: true,
  },
  tooltipText: {
    type: String,
    default: '',
  },
  noLabels: {
    type: Boolean,
    default: false,
  },
});

const computedReportId = computed(() => props.reportId || nanoid(7));
const loadedAndReturnedData = computed(() => props.loadedData && !props.dataEmpty);
const loadedAndNotReturnedData = computed(() => props.loadedData && props.dataEmpty);
</script>

<style scoped lang="scss">
@use '@/features/reports/components/reports.scss' as *;

.report-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%; /* Inherit full height from .island */
}

.header {
  flex-shrink: 0; /* Prevent header from shrinking */
}

.chart-container {
  flex: 1; /* Grow to fill remaining space */
  position: relative; /* For chartjs-size-monitor */
  min-height: 0; /* Prevent flex overflow issues */
}

.chart-inner {
  width: 100%;
  height: 100%; /* Fill the .chart-container */
  display: flex;
}

.chart-labels {
  flex-shrink: 0; /* Prevent labels from shrinking */
}

.no-data,
.error-overlay {
  min-width: 237px;
  flex: 1; /* Ensure these fill space when chart isn’t present */
}

.error-overlay {
  background: #f9f9f9;
}

.header {
  .title-container {
    .icon {
      font-size: 1.1rem;
      height: 1rem;
    }
  }
}

:deep(.sk-folding-cube) {
  margin: 6rem auto;
}
</style>
